'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

import {
  detectRegion,
  getConsent,
  saveConsent,
  createConsentRecord,
  type ConsentRegion,
} from '@/lib/consent';
import ConsentPreferenceCenter from './ConsentPreferenceCenter';

export default function CookieBanner() {
  const [region, setRegion] = useState<ConsentRegion>('none');
  const [hasConsent, setHasConsent] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const detectedRegion = detectRegion();
    setRegion(detectedRegion);

    const existingConsent = getConsent();
    if (existingConsent) {
      setHasConsent(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const record = createConsentRecord(region, {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    });
    saveConsent(record);
    setHasConsent(true);
  };

  const handleRejectAll = () => {
    const record = createConsentRecord(region, {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    });
    saveConsent(record);
    setHasConsent(true);
  };

  const handleCCPADismiss = () => {
    const record = createConsentRecord(region, {
      necessary: true,
      analytics: true,
      marketing: false,
      preferences: true,
    });
    saveConsent(record);
    setHasConsent(true);
  };

  // GDPR Banner
  if (region === 'gdpr' && !hasConsent) {
    return (
      <>
        <div
          className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-lg"
          role="region"
          aria-label="Cookie consent banner"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-2">
                  We respect your privacy
                </h2>
                <p className="text-sm text-slate-600 mb-3">
                  We use cookies and similar technologies to understand how you use our site,
                  improve your experience, and deliver personalized content. By clicking "Accept All", you consent to all cookies. You can manage your preferences by clicking "Manage Preferences" or visit our{' '}
                  <Link href="/privacy" className="text-teal-600 hover:underline">
                    Privacy Policy
                  </Link>
                  {' '}and{' '}
                  <Link href="/cookies" className="text-teal-600 hover:underline">
                    Cookie Policy
                  </Link>
                  .
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  Reject All
                </button>
                <button
                  onClick={() => setShowPreferences(true)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  Manage Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
        {showPreferences && (
          <ConsentPreferenceCenter
            isOpen={showPreferences}
            onClose={() => setShowPreferences(false)}
            onSave={() => setHasConsent(true)}
          />
        )}
      </>
    );
  }

  // CCPA Banner
  if (region === 'ccpa' && !hasConsent) {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-lg"
        role="region"
        aria-label="Cookie notice"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-slate-600">
              We use cookies to enhance your experience. By continuing to browse, you consent to
              our use of cookies. See our{' '}
              <Link href="/privacy#do-not-sell" className="text-teal-600 hover:underline">
                Do Not Sell My Personal Information
              </Link>
              .
            </p>
            <button
              onClick={handleCCPADismiss}
              className="flex-shrink-0 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
